import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { Icon } from 'semantic-ui-react';
import { makeAssetLink } from '../utils/helpers';
import {
  AUTH_MODES,
  MIGRATION_STATUS,
  getAuthMode,
  isValidEmail,
  fetchAccountStatus,
  startMigration,
  startSsoLogin,
} from '../utils/helpers/ssoAuth';

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 460,
    borderRadius: 8,
    border: 'none',
    padding: '2.5rem 2rem',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 99,
  },
};

const styles = {
  logo: { display: 'block', maxWidth: 240, maxHeight: 140, margin: '0 auto 2rem' },
  form: { position: 'relative', maxWidth: 340, margin: '0 auto' },
  input: {
    width: '100%',
    height: 56,
    padding: '0 48px 0 16px',
    fontSize: 16,
    border: '1px solid #DEDEDF',
    borderRadius: 5,
    boxSizing: 'border-box',
  },
  arrowButton: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
  },
  validation: { color: '#9f3a38', textAlign: 'center', marginTop: 12 },
  centerText: { textAlign: 'center' },
  linksRow: { marginTop: 24, textAlign: 'center' },
  link: { color: '#4183c4', cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit' },
  choiceButton: {
    display: 'block',
    width: '100%',
    maxWidth: 340,
    margin: '12px auto 0',
    padding: '12px 16px',
    borderRadius: 5,
    border: '1px solid #DEDEDF',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 15,
  },
};

const VIEW = {
  EMAIL: 'email',
  CHOICE: 'choice',
  MIGRATE: 'migrate',
  MIGRATE_SENT: 'migrateSent',
  ERROR: 'error',
};

/**
 * Native email-entry login screen (MP-2328). Replaces the ias-connect iframe for marketplaces whose
 * AuthMode is not "Current". Collects the email, asks marketplace-api whether the account is migrated,
 * then routes per the AuthMode matrix:
 *   - migrated              -> SSO (popup) -> legacy-ticket exchange -> app
 *   - not migrated + MixedNoChoice -> ias-connect (existing iframe)
 *   - not migrated + MixedChoice   -> choose migrate or continue on ias-connect
 *   - not migrated + Forced        -> migration path (deferred; placeholder)
 */
export default function EmailLoginModal(props) {
  const { isOpen, onClose, onUseIasConnect, onRegister, showRegister } = props;

  const [view, setView] = useState(VIEW.EMAIL);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  // Reset to a clean email view whenever the modal is closed, so reopening never lands on a stale
  // choice/migrate/error view.
  useEffect(() => {
    if (!isOpen) {
      setView(VIEW.EMAIL);
      setEmail('');
      setBusy(false);
      setMessage('');
    }
  }, [isOpen]);

  const resetAndClose = () => {
    onClose();
  };

  const startSso = async () => {
    setBusy(true);
    setMessage('');
    try {
      // Popup when embedded in an iframe, top-level redirect otherwise. On success this navigates
      // away (popup: after the legacy-ticket exchange; redirect: to the IdP), so nothing after runs.
      await startSsoLogin();
    } catch (err) {
      setBusy(false);
      setView(VIEW.ERROR);
      if (err && err.message === 'popup_blocked') {
        setMessage('Please allow popups for this site to sign in.');
      } else if (err && err.message === 'popup_closed') {
        setMessage('Sign-in was canceled.');
      } else {
        setMessage('We couldn’t complete sign-in. Please try again.');
      }
    }
  };

  /**
   * Ask marketplace-api to provision the account in Auth0 (via the CIAM platform), which triggers the
   * set-password email. An already-migrated account skips straight to SSO — that can happen if the user
   * migrated from another marketplace between entering their email and landing here.
   */
  const submitMigration = async () => {
    setBusy(true);
    setMessage('');
    try {
      const status = await startMigration(email.trim());
      if (status === MIGRATION_STATUS.ALREADY_MIGRATED) {
        await startSso();
        return;
      }
      setBusy(false);
      setView(VIEW.MIGRATE_SENT);
    } catch (err) {
      setBusy(false);
      // Stay on the current view so the user can retry without re-entering their email.
      setMessage(
        err && err.message === 'migration_failed_429'
          ? 'Too many attempts. Please wait a few minutes and try again.'
          : 'We couldn’t set up your new sign-in. Please try again.',
      );
    }
  };

  const submitEmail = async e => {
    if (e) e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setMessage('Please enter a valid email address');
      return;
    }
    setMessage('');
    setBusy(true);
    try {
      const migrated = await fetchAccountStatus(trimmed);
      if (migrated) {
        await startSso();
        return;
      }
      const mode = getAuthMode();
      switch (mode) {
        case AUTH_MODES.SSO:
          // Login.js sends Sso marketplaces straight to the IdP, so this modal should never open for
          // them. Handled explicitly anyway: the default branch below is ias-connect, which would be
          // the wrong destination for a marketplace whose users have no ias-connect credential.
          await startSso();
          break;
        case AUTH_MODES.MIXED_CHOICE:
          setBusy(false);
          setView(VIEW.CHOICE);
          break;
        case AUTH_MODES.FORCED:
          setBusy(false);
          setView(VIEW.MIGRATE);
          break;
        case AUTH_MODES.MIXED_NO_CHOICE:
        default:
          // Hand off to the existing ias-connect iframe flow.
          onUseIasConnect(trimmed);
          break;
      }
    } catch (err) {
      setBusy(false);
      setView(VIEW.ERROR);
      setMessage('We couldn’t start sign-in. Please try again.');
    }
  };

  const renderEmailView = () => (
    <>
      <form style={styles.form} onSubmit={submitEmail}>
        <input
          autoFocus
          type="email"
          name="email"
          placeholder="Email"
          value={email}
          disabled={busy}
          onChange={e => setEmail(e.target.value)}
          style={styles.input}
        />
        <button type="submit" style={styles.arrowButton} disabled={busy || email === ''} aria-label="Continue">
          <Icon
            name={busy ? 'spinner' : 'arrow alternate circle right outline'}
            loading={busy}
            size="large"
            link
          />
        </button>
      </form>
      {message && <p style={styles.validation}>{message}</p>}
      {/* TODO(MP-2328): social sign-in (Google/Apple) and password reset are still handled by
          ias-connect; wire them here when the native flows exist. */}
      {showRegister && (
        <div style={styles.linksRow}>
          <span>Need an account? </span>
          <button type="button" style={styles.link} onClick={onRegister}>
            Register
          </button>
        </div>
      )}
    </>
  );

  const renderChoiceView = () => (
    <div style={styles.centerText}>
      <p>How would you like to sign in?</p>
      <button type="button" style={styles.choiceButton} onClick={() => setView(VIEW.MIGRATE)}>
        Set up the new sign-in
      </button>
      <button type="button" style={styles.choiceButton} onClick={() => onUseIasConnect(email.trim())}>
        Continue with my current sign-in
      </button>
    </div>
  );

  const renderMigrateView = () => (
    <div style={styles.centerText}>
      <p>
        This marketplace is moving to a new, more secure sign-in. We&apos;ll email{' '}
        <strong>{email.trim()}</strong> a link to set your password — it only takes a minute, and you
        only have to do it once.
      </p>
      <button type="button" style={styles.choiceButton} onClick={submitMigration} disabled={busy}>
        {busy ? 'Setting up…' : 'Email me a set-up link'}
      </button>
      {/* Forced mode has no way back to ias-connect, so only offer the escape hatch when the
          marketplace actually allows the user to keep their current sign-in. */}
      {getAuthMode() === AUTH_MODES.MIXED_CHOICE && (
        <button
          type="button"
          style={styles.choiceButton}
          onClick={() => onUseIasConnect(email.trim())}
          disabled={busy}
        >
          Not now — use my current sign-in
        </button>
      )}
      {message && <p style={styles.validation}>{message}</p>}
    </div>
  );

  const renderMigrateSentView = () => (
    <div style={styles.centerText}>
      <p>
        Check your inbox — we&apos;ve sent a link to <strong>{email.trim()}</strong>. Follow it to set
        your password, then come back here and sign in.
      </p>
      <button type="button" style={styles.choiceButton} onClick={startSso} disabled={busy}>
        {busy ? 'Signing in…' : 'I’ve set my password — sign in'}
      </button>
      <div style={styles.linksRow}>
        <span>Didn&apos;t get it? </span>
        <button type="button" style={styles.link} onClick={submitMigration} disabled={busy}>
          Send it again
        </button>
      </div>
      {message && <p style={styles.validation}>{message}</p>}
    </div>
  );

  const renderErrorView = () => (
    <div style={styles.centerText}>
      <p style={styles.validation}>{message}</p>
      <button type="button" style={styles.choiceButton} onClick={() => { setView(VIEW.EMAIL); setMessage(''); }}>
        Try again
      </button>
    </div>
  );

  const renderBody = () => {
    switch (view) {
      case VIEW.CHOICE:
        return renderChoiceView();
      case VIEW.MIGRATE:
        return renderMigrateView();
      case VIEW.MIGRATE_SENT:
        return renderMigrateSentView();
      case VIEW.ERROR:
        return renderErrorView();
      case VIEW.EMAIL:
      default:
        return renderEmailView();
    }
  };

  return (
    <Modal
      id="mp-email-login-modal"
      isOpen={isOpen}
      style={modalStyles}
      contentLabel="Sign in"
      onRequestClose={resetAndClose}
      ariaHideApp={false}
    >
      <img alt="" src={makeAssetLink('logo.png')} style={styles.logo} />
      {renderBody()}
    </Modal>
  );
}

EmailLoginModal.defaultProps = {
  onClose: () => null,
  onUseIasConnect: () => null,
  onRegister: () => null,
  showRegister: true,
};

EmailLoginModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onUseIasConnect: PropTypes.func,
  onRegister: PropTypes.func,
  showRegister: PropTypes.bool,
};
